import { OUT_OF_SCOPE_CODE, OUT_OF_SCOPE_MESSAGE } from "./member-access";
import { Field, fieldLabel, humanize } from "./schema-field";

/**
 * Postgres / Nest write errors the live API still returns as raw `pg` strings.
 *
 * | raw | field | message |
 * | duplicate key … + Key (slug)=(foo) already exists | slug if in fields | {Label} is already taken |
 * | null value in column "name" … not-null | name | {Label} is required |
 * | FK + Key (project_id)=(99) is not present | project_id | {Label} does not match an existing record |
 * | update or delete on table … FK | (omit) | Other records still point at this one, so it cannot be deleted |
 * | invalid input syntax for type integer/uuid/boolean/numeric/date/timestamp | omit unless a column name is present | {value} is not a valid {type} |
 * | value too long for type character varying(40) | omit unless Key/column present | That value is too long for this column |
 * | anything else | omit | short human sentence, else Could not save this record |
 *
 * `field` is set only when it equals a provided schema `name` (exact).
 */

export type PgWriteError = { field?: string; message: string };

const FALLBACK = "Could not complete that change";
const DELETE_FK =
  "Other records still point at this one, so it cannot be deleted";
const TOO_LONG = "That value is too long for this column";

const TYPE_TOKEN =
  "integer|uuid|boolean|numeric|date|timestamp(?:\\s+with(?:out)?\\s+time\\s+zone)?";

const looksRaw = (text: string): boolean =>
  /violates|\bSQL\b|constraint|^ERROR:/i.test(text);

const namedFields = (
  fields?: Array<{ name: string }>
): Array<{ name: string }> => fields ?? [];

const resolveField = (
  candidate: string | undefined,
  fields?: Array<{ name: string }>
): string | undefined => {
  if (!candidate) return undefined;
  return namedFields(fields).some((field) => field.name === candidate)
    ? candidate
    : undefined;
};

const labelFor = (name: string, fields?: Array<{ name: string }>): string => {
  const match = namedFields(fields).find((field) => field.name === name);
  if (match) return fieldLabel(match as Field);
  return humanize(name);
};

/** `Key (slug)=(foo)` or `Key (org_id, slug)=(1, foo)`. */
const keyColumns = (text: string): string[] => {
  const match = text.match(/Key\s*\(([^)]+)\)\s*=/i);
  if (!match) return [];
  return match[1]
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

const firstMatchingKey = (
  text: string,
  fields?: Array<{ name: string }>
): string | undefined => {
  for (const key of keyColumns(text)) {
    const resolved = resolveField(key, fields);
    if (resolved) return resolved;
  }
  return undefined;
};

const quotedColumn = (text: string): string | undefined => {
  const match = text.match(/column\s+"([^"]+)"/i);
  return match?.[1];
};

const firstNamedColumn = (
  text: string,
  fields?: Array<{ name: string }>
): string | undefined => {
  const fromKey = firstMatchingKey(text, fields);
  if (fromKey) return fromKey;
  return resolveField(quotedColumn(text), fields);
};

const displayType = (raw: string): string => {
  const lower = raw.toLowerCase();
  if (lower.startsWith("timestamp")) return "timestamp";
  return lower;
};

const asText = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

/**
 * Nest/axios puts a string or string[] on `response.data.message`.
 * Arrays are joined so a DETAIL line still sits next to the headline.
 */
const messageFrom = (raw: unknown): string => {
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (Array.isArray(raw)) {
    const joined = raw.map(asText).filter(Boolean).join("\n");
    if (joined) return joined;
  }
  return "";
};

export const extractWriteErrorText = (err: unknown): string => {
  const bag = err as {
    response?: { data?: { message?: unknown } };
    error?: { message?: unknown };
    message?: unknown;
  };
  return (
    messageFrom(bag?.response?.data?.message) ||
    messageFrom(bag?.error?.message) ||
    (err instanceof Error ? err.message.trim() : messageFrom(bag?.message)) ||
    ""
  );
};

export const parsePgWriteError = (
  text: string,
  fields?: Array<{ name: string }>
): PgWriteError => {
  const src = (text ?? "").trim();
  if (!src) return { message: FALLBACK };

  if (/duplicate key value violates unique constraint/i.test(src)) {
    const keys = keyColumns(src);
    const field =
      resolveField("slug", fields) && keys.includes("slug")
        ? "slug"
        : firstMatchingKey(src, fields) ??
          resolveField(keys[0], fields);
    const name = field ?? keys[0];
    if (name) {
      return {
        field,
        message: `${labelFor(name, fields)} is already taken`,
      };
    }
    return { message: "That value is already taken" };
  }

  const notNull = src.match(
    /null value in column "([^"]+)"[\s\S]*violates not-null constraint/i
  );
  if (notNull) {
    const name = notNull[1];
    return {
      field: resolveField(name, fields),
      message: `${labelFor(name, fields)} is required`,
    };
  }

  if (
    /update or delete on table\s+"[^"]+"\s+violates foreign key constraint/i.test(
      src
    ) ||
    /is still referenced from table/i.test(src)
  ) {
    return { message: DELETE_FK };
  }

  if (
    /violates foreign key constraint/i.test(src) &&
    /is not present/i.test(src)
  ) {
    const keys = keyColumns(src);
    const field = firstMatchingKey(src, fields) ?? resolveField(keys[0], fields);
    const name = field ?? keys[0];
    if (name) {
      return {
        field,
        message: `${labelFor(name, fields)} does not match an existing record`,
      };
    }
    return {
      message: "That value does not match an existing record",
    };
  }

  const typeMatch = src.match(
    new RegExp(
      `invalid input syntax for type\\s+(${TYPE_TOKEN})\\s*:\\s*"([^"]*)"`,
      "i"
    )
  );
  if (typeMatch) {
    const type = displayType(typeMatch[1]);
    const value = typeMatch[2];
    const message = value
      ? `${value} is not a valid ${type}`
      : `That value is not a valid ${type}`;
    return {
      field: firstNamedColumn(src, fields),
      message,
    };
  }

  if (/value too long for type character varying/i.test(src)) {
    return {
      field: firstNamedColumn(src, fields),
      message: TOO_LONG,
    };
  }

  if (src.length < 120 && !looksRaw(src)) {
    return { message: src };
  }

  return { message: FALLBACK };
};

const structuredWriteError = (
  err: unknown,
  fields?: Array<{ name: string }>
): PgWriteError | undefined => {
  const data = (err as { response?: { data?: unknown } })?.response?.data as
    | { message?: unknown; field?: unknown }
    | undefined;
  if (!data || typeof data.field !== "string") return undefined;
  const message = messageFrom(data.message);
  if (!message) return undefined;
  const field = resolveField(data.field, fields);
  return field ? { field, message } : { message };
};

const responseCode = (err: unknown): string => {
  const code = (err as { response?: { data?: { code?: unknown } } })?.response
    ?.data?.code;
  return typeof code === "string" ? code : "";
};

export const describeWriteError = (
  err: unknown,
  fields?: Array<{ name: string }>
): PgWriteError => {
  // A save that would leave the member's row filter. No field owns this:
  // the form shows it once, next to the form, not under a column.
  if (responseCode(err) === OUT_OF_SCOPE_CODE) {
    return { message: OUT_OF_SCOPE_MESSAGE };
  }
  return (
    structuredWriteError(err, fields) ??
    parsePgWriteError(extractWriteErrorText(err), fields)
  );
};
